import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { RadioButton } from 'src/components/radioButton/RadioButton';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const GraphVisibilityModal = ({
  isOpen,
  onClose,
  graphId,
  hiddenFromUninvitedNonAdminAccountMembers:
    defaultHiddenFromUninvitedNonAdminAccountMembers,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  hiddenFromUninvitedNonAdminAccountMembers: boolean;
}) => {
  const [updateHiddenFromUninvitedNonAdminAccountMembers, { loading }] =
    useMutation<
      GraphQLTypes.SetIsGraphHiddenFromUninvitedNonAdminAccountMembersMutation,
      GraphQLTypes.SetIsGraphHiddenFromUninvitedNonAdminAccountMembersMutationVariables
    >(
      gql`
        mutation SetIsGraphHiddenFromUninvitedNonAdminAccountMembersMutation(
          $hiddenFromUninvitedNonAdminAccountMembers: Boolean!
          $graphId: ID!
        ) {
          service(id: $graphId) {
            updateHiddenFromUninvitedNonAdminAccountMembers(
              hiddenFromUninvitedNonAdminAccountMembers: $hiddenFromUninvitedNonAdminAccountMembers
            ) {
              id
              hiddenFromUninvitedNonAdminAccountMembers
            }
          }
        }
      `,
      { onCompleted: () => onClose() },
    );

  const [
    hiddenFromUninvitedNonAdminAccountMembers,
    setHiddenFromUninvitedNonAdminAccountMembers,
  ] = useState(defaultHiddenFromUninvitedNonAdminAccountMembers);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <Form
        onSubmit={async () => {
          await updateHiddenFromUninvitedNonAdminAccountMembers({
            variables: {
              graphId,
              hiddenFromUninvitedNonAdminAccountMembers,
            },
          });
        }}
        noValidate={true}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Change your graph’s default visibility</ModalTitle>
            <ModalDescription>
              These settings will apply to org members by default, and can be
              overridden by custom org and graph level settings.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <RadioButton
              title="Visible"
              description="Your graph will be visible to other members of this organization by default."
              disabled={loading}
              selected={!hiddenFromUninvitedNonAdminAccountMembers}
              onClick={() => {
                setHiddenFromUninvitedNonAdminAccountMembers(false);
              }}
            />
            <RadioButton
              title="Hidden"
              description="Your graph will be hidden from other members of this organization unless they are added to the graph."
              disabled={loading}
              selected={hiddenFromUninvitedNonAdminAccountMembers}
              onClick={() => {
                setHiddenFromUninvitedNonAdminAccountMembers(true);
              }}
            />
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={loading}
                isLoading={loading}
                loadingText="Confirm"
              >
                Confirm
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
